/**
 * Сверка индекса и замок исхода окна: пороги, тон и подписи.
 *
 * Модуль ЧИСТЫЙ, как lib/valuation.ts, и по той же причине: компонентных тестов
 * в проекте нет, поэтому всё, о чём можно ошибиться, обязано жить в .ts.
 *
 * # Что тут за физика
 *
 * Отчёт TWAP опаздывает на три секунды: значение с меткой t — это среднее сырого
 * ряда по `[t−W−2, t−3]`. Значит за τ секунд до конца окна неизвестны только
 * `τ−3` секунды из W, и перевернуть исход может лишь скачок `W·C/(τ−3)`,
 * УДЕРЖАННЫЙ весь остаток. Разбор с числами — docs/polymarket-integration.md,
 * сторож тождества — `make gate-twap-lag`.
 *
 * # Чего эта панель НЕ утверждает
 *
 * Все числа здесь — про значение, которое раздаёт Polymarket. Что оно совпадает
 * с подписанным отчётом Chainlink, не проверено и изнутри провода не
 * проверяется. Поэтому SOURCE_NOTE присутствует на экране всегда — значком «ⓘ»
 * рядом с замком и в плашке тревоги, с текстом по наведению и целиком в окне
 * разбора. Значок, а не строка: место в липкой полосе оплачено всей высотой
 * экрана, а прочитывают оговорку один раз.
 */

import type {
  IndexForecast,
  LockState,
  TwapCheck,
  Valuation,
  WindowFinal,
  WindowLock,
} from "../api/types";
import { parse, SCALE } from "./money";

/**
 * Оговорка о происхождении числа.
 *
 * Без неё аларм читается как гарантия — а это утверждение о числе биржи, а не о
 * подписанном отчёте оракула. Как именно она попадает на экран — см. докблок
 * модуля: значок на панели, полный текст в окне разбора.
 */
export const SOURCE_NOTE =
  "пересчёт раздачи Polymarket; с подписанным отчётом Chainlink не сверялось";

/** Уровень тревоги панели. */
export type LockLevel = "none" | "warn" | "alarm";

export interface LockView {
  level: LockLevel;
  /** Строка для оператора: что именно происходит. */
  label: string;
  /** Чем мерили порог — чтобы «предупреждение» не выглядело мнением. */
  basis: string;
}

/**
 * Замок словами и цветом.
 *
 * # Решение приходит С СЕРВЕРА, а не считается здесь
 *
 * Прежде эта функция сама сравнивала требуемый скачок с порогом режима. Так
 * больше нельзя, и причина не в стиле: ТО ЖЕ САМОЕ решение принимает подсистема
 * алертов в трейдере, поднимая сквозную карточку со сделкой. Две реализации
 * одного замка разошлись бы на первой же правке — причём ТИХО, потому что оба
 * ответа выглядят правдоподобно, — и панель говорила бы об окне одно, а карточка
 * другое. Общая реализация живёт в services/internal/lock; здесь остаётся
 * перевод состояния в цвет.
 *
 * Аларм при этом по-прежнему не зависит от режима: это арифметика, а не оценка.
 */
export function lockView(v: Valuation | undefined): LockView {
  const lock = v?.lock;
  if (!lock) {
    return { level: "none", label: lockAbsentReason(v), basis: "" };
  }
  return {
    level: levelOf(lock.state),
    label: lock.label,
    basis: lock.basis ?? "",
  };
}

/**
 * Состояние сервера в уровень тревоги панели.
 *
 * Имена разные намеренно: у сервера это СОСТОЯНИЕ окна («исход определён»), у
 * панели — уровень тревоги («аларм»), и на карточке замка то же состояние
 * называется третьим способом. Свести их к одному слову значило бы либо назвать
 * цвет состоянием, либо состояние цветом.
 */
function levelOf(state: LockState | undefined): LockLevel {
  switch (state) {
    case "settled":
      return "alarm";
    case "safe":
      return "warn";
    default:
      return "none";
  }
}

/**
 * Почему замка нет — НАЗЫВАЯ причину, а не пустым местом.
 *
 * Причин ровно три, и они требуют разных действий оператора, поэтому общей
 * фразой не отделаться. Самая частая — страйк: записать его можно только живьём,
 * на секунде начала окна, поэтому перезапуск трейдера посреди окна оставляет это
 * окно без замка навсегда, а следующее считается как ни в чём не бывало.
 */
function lockAbsentReason(v: Valuation | undefined): string {
  if (!v) return "замок не считается: данных ещё нет";
  if (!v.strike) {
    return "замок не считается: страйк окна не записан — трейдер не застал его начала";
  }
  if (!v.check) return "замок не считается: сырой ряд не приехал";
  return "замок не считается: ряда не хватает на итоговое окно";
}

/**
 * Пропажа в итоговом окне словами. Пусто, когда терять ещё нечего.
 *
 * Тревожит ТОЛЬКО настоящая пропажа — секунда прошла, а числа нет. Пока окно не
 * наступило, оно неполно по определению, и надпись об этом горела бы на экране
 * все пять минут подряд, то есть отучила бы себя читать. Ровно то же правило,
 * по которому свежий возраст индекса не показывается вовсе.
 */
export function coverageNote(lock: WindowLock | undefined): string {
  const lost = lock?.lost ?? 0;
  if (lost <= 0) return "";
  return `в итоговом окне потеряно секунд: ${lost}`;
}

/**
 * Сколько секунд итогового окна ещё неизвестно, для показа.
 *
 * Миллисекунды приходят с сервера, а на экран идут секунды: доля секунды здесь
 * ничего не решает, а мелькающие цифры мешают читать.
 */
export function unknownSeconds(lock: WindowLock | undefined): number | null {
  if (!lock) return null;
  return Math.max(0, Math.ceil(lock.unknown_ms / 1000));
}

/**
 * Началось ли итоговое окно, то есть идёт ли обратный отсчёт замка.
 *
 * Пока не началось, «неизвестно» прибито к ширине окна и стоит на месте четыре
 * с половиной минуты из пяти — число, которое не меняется и потому ничего не
 * сообщает. Отличать эти два состояния приходится, чтобы не показывать
 * замерший счётчик как живой.
 */
export function lockCounting(v: Valuation | undefined, lock: WindowLock | undefined): boolean {
  const windowS = v?.window_s ?? 0;
  if (!lock || windowS <= 0) return false;
  return lock.unknown_ms < windowS * 1000;
}

/**
 * Мёртвый префикс: сколько секунд ход обязан ПРОДЕРЖАТЬСЯ, чтобы попасть в итог.
 *
 * # Почему не «через сколько начнётся итоговое окно»
 *
 * Число то же, но вопрос другой, и оператор задаёт именно второй. Пока итоговое
 * окно впереди, ход, откатившийся за это время, вносит в исход РОВНО НОЛЬ: при
 * W = 60 и τ = 90 бесполезен любой ход короче 27 секунд. «Начнётся через 27 с»
 * читается как ожидание, «продержаться 27 с» — как требование, и второе и есть
 * то, что надо знать до решения.
 *
 * # Почему с сервера, а не отсюда
 *
 * Прежде эта функция вычитала задержку публикации сама (`seconds_left − W − 3`)
 * и тем держала копию `twap.Delay` в TypeScript. Задержка — измеренное свойство
 * источника, а не константа интерфейса: разъехавшись с сервером на одну
 * секунду, панель показывала бы другое требование, чем то, по которому считан
 * замок, — и разошлись бы они ТИХО.
 *
 * null означает «итоговое окно уже идёт»: держаться больше не надо, попадёт
 * любой ход.
 */
export function deadPrefixSeconds(lock: WindowLock | undefined): number | null {
  const ms = lock?.dead_prefix_ms ?? 0;
  if (ms <= 0) return null;
  return Math.ceil(ms / 1000);
}

/**
 * Совпадает ли требуемый скачок с запасом.
 *
 * Совпадает он не случайно, а ПО ПОСТРОЕНИЮ, пока неизвестно всё окно: при
 * `u = W` формула `M = W·C/u` даёт ровно `C`. То есть две строки на экране
 * четыре с половиной минуты из пяти показывают одно и то же число, и вторая из
 * них не сообщает ничего.
 */
export function requiredEqualsCushion(lock: WindowLock | undefined): boolean {
  const required = parse(lock?.required ?? "");
  const cushion = parse(lock?.cushion ?? "");
  if (required === null || cushion === null || !lock?.possible) return false;
  return required === Math.abs(cushion);
}

/**
 * Покрытие окна пересчёта: «30 из 30» или пусто, когда всё на месте.
 *
 * Пусто НАМЕРЕННО: полное покрытие — норма, и подпись о нём на каждом обновлении
 * приучает не читать это место вовсе. Ровно то же правило, по которому свежий
 * возраст индекса не показывается.
 */
export function coverageLabel(check: TwapCheck | undefined): string {
  if (!check || !check.own_want) return "";
  const have = check.own_have ?? 0;
  if (have >= check.own_want) return "";
  return `${have} из ${check.own_want}`;
}

/**
 * Точность пересчёта словами.
 *
 * Доля точных считается по ПОЛНЫМ окнам: на окне из 25–29 снимков вместо
 * тридцати среднее до цента не сойдётся никогда, и разбавленная доля мерила бы
 * пропуски источника, а не свойство отчёта.
 */
export function accuracyLabel(check: TwapCheck | undefined): string {
  if (!check) return "нет данных";
  const full = check.full ?? 0;
  if (full === 0) return "ещё не сверялось на полном окне";
  const exact = check.exact ?? 0;
  const share = Math.round((exact / full) * 100);
  // Срок называется обязательно: без него дробь не значит ничего — знаменатель
  // зависит от того, сколько ряда накоплено, и молча растёт после старта.
  const span = check.span_s ? ` за ${Math.round(check.span_s / 60)} мин` : "";
  // Единица у медианы обязательна: это ДОЛЛАРЫ расхождения, а рядом в той же
  // строке стоят проценты и штуки — без подписи число читается как что угодно.
  return (
    `${exact} из ${full} полных окон до цента (${share} %)${span}, ` +
    `медиана расхождения ${fmtUsd(parse(check.median ?? "") ?? 0)} $`
  );
}

/**
 * Тон расхождения пересчёта с числом источника.
 *
 * Порог — полдоллара, и это не выдумка: при верном сдвиге окна медиана
 * расхождения по полным окнам держалась нулевой, а соседний сдвиг давал уже
 * треть доллара. Полдоллара означает «сошлось не так, как должно».
 */
export const DIFF_ALARM_USD = "0.5";

export function diffTone(check: TwapCheck | undefined): "ok" | "warn" {
  const diff = parse(check?.diff ?? "");
  const limit = parse(DIFF_ALARM_USD);
  if (diff === null || limit === null) return "ok";
  return Math.abs(diff) > limit ? "warn" : "ok";
}

/** Разность «наш пересчёт минус источник» со знаком, для показа. */
export function diffLabel(check: TwapCheck | undefined): string {
  const diff = parse(check?.diff ?? "");
  if (diff === null) return "—";
  // Знак ставится по ОКРУГЛЁННОМУ значению, а не по исходному: расхождение в
  // одну миллионную доллара — это совпадение, и «−0.00» на экране читается как
  // дефект показа, а не как точное попадание.
  const rounded = fmtUsd(Math.abs(diff));
  if (rounded === "0.00") return "0.00";
  return `${diff > 0 ? "+" : "−"}${rounded}`;
}

/**
 * Насколько сдвинется число источника, когда он дойдёт до нашей метки.
 *
 * Это и есть фора, выраженная в деньгах: значение мы уже знаем, источник — ещё
 * нет. Пусто, когда сдвиг округляется в ноль: рынок стоит, и объявлять об этом
 * нечего.
 */
export function aheadLabel(check: TwapCheck | undefined): string {
  const diff = parse(check?.ahead_diff ?? "");
  if (diff === null) return "";
  const rounded = fmtUsd(Math.abs(diff));
  if (rounded === "0.00") return "источник придёт к тому же числу";
  return `${diff > 0 ? "+" : "−"}${rounded} $ к числу источника`;
}

/**
 * Сверка страйка с биржей словами. Пусто, когда говорить не о чем.
 *
 * Три разных состояния, и молчит только первое:
 *
 *   сошлось          — норма, подпись на каждом окне приучала бы не читать;
 *   расходится       — оценка окна смещена ровно на эту величину;
 *   дозаписан        — страйк не наш: начала окна мы не застали.
 *
 * «Не сверялось» тоже молчит: маршрут сверки чужой и может не ответить, а
 * тревожить оператора отсутствием ответа не за что.
 */
export function strikeCheckNote(v: Valuation | undefined): string {
  if (!v?.strike) return "";
  if (v.strike_from_exchange) {
    return "страйк дозаписан с биржи: начала окна трейдер не застал";
  }
  const diff = parse(v.strike_exchange_diff ?? "");
  if (diff === null || !v.strike_exchange) return "";
  const rounded = fmtUsd(Math.abs(diff));
  if (rounded === "0.00") return "";
  return `расходится с биржей на ${diff > 0 ? "+" : "−"}${rounded} $`;
}

/** Тон сверки страйка: расхождение красится тревожно, дозапись — нет. */
export function strikeCheckTone(v: Valuation | undefined): "flat" | "warn" {
  if (!v?.strike || v.strike_from_exchange) return "flat";
  const diff = parse(v.strike_exchange_diff ?? "");
  if (diff === null) return "flat";
  return fmtUsd(Math.abs(diff)) === "0.00" ? "flat" : "warn";
}

export interface CheckSummary {
  tone: "ok" | "warn";
  /**
   * Наш отчёт на метку, которой источник ЕЩЁ НЕ ПУБЛИКОВАЛ. Сырая строка: на
   * экран идёт через formatIndex, тем же форматом, что страйк и индекс.
   */
  value: string | undefined;
  /** Насколько сдвинется число источника, когда дойдёт до нашей метки. */
  drift: string;
  /** Что не так со сверкой; пусто, когда сошлось. */
  note: string;
  /** Полное объяснение — в подсказку строки. */
  title: string;
}

/**
 * Итог яруса сверки: НАШ ОПЕРЕЖАЮЩИЙ ОТЧЁТ и тон доверия к нему.
 *
 * # Почему на экране предикшен, а не расхождение
 *
 * Расхождение («наш пересчёт минус опубликованное») отвечает на вопрос
 * «сходится ли наша арифметика» — это диагностика, её читают раз в сеанс. А
 * решение принимают по ЧИСЛУ: где метрика окажется через три секунды, когда
 * источник её опубликует. Оно того же рода, что страйк и индекс ярусом выше, и
 * встаёт с ними в один столбик — сравнивают их глазом по вертикали.
 *
 * Это не прогноз: отчёту с меткой t нужен сырой ряд только до t−3, то есть все
 * входы уже существуют (см. TwapCheck.ahead).
 *
 * # Почему тон поднимают ДВЕ разные проверки
 *
 * Расхождение пересчёта и расхождение страйка — про разное («сходится ли наша
 * арифметика» против «тот ли у нас страйк»), но следствие у них одно: числам
 * окна верить нельзя, в том числе и предикшену. Сжав ярус до одного числа, мы
 * обязаны поднять наверх обе, иначе тревога исчезла бы с экрана вместе со
 * строками, которые её несли.
 */
export function checkSummary(v: Valuation | undefined): CheckSummary {
  const check = v?.check;
  if (!check) {
    // Причина словами и тревожным тоном: пустота на месте числа читается как
    // ноль, а «сверка не идёт» — это не «сверка сошлась».
    return {
      tone: "warn",
      value: undefined,
      drift: "",
      note: emptyNote(v) || "сверять нечего",
      title: SOURCE_NOTE,
    };
  }

  const diffWarn = diffTone(check) === "warn";
  const strikeWarn = strikeCheckTone(v) === "warn";

  // Порядок замечаний — по тому, что раньше обесценивает решение: смещённый
  // страйк ломает оценку ВСЕГО окна, а расхождение пересчёта — только нашу
  // фору. Показывается одно: два замечания в строке карточки читаются как
  // список, а действие по обоим одно и то же — открыть разбор.
  const note = strikeWarn
    ? strikeCheckNote(v)
    : diffWarn
      ? `сверка расходится больше чем на ${DIFF_ALARM_USD} $`
      : "";

  return {
    tone: diffWarn || strikeWarn ? "warn" : "ok",
    value: check.ahead,
    drift: aheadLabel(check),
    note,
    title:
      "отчёт, которого источник ещё не публиковал: ему нужен сырой ряд только до " +
      "t−3, поэтому по нашему ряду метка считается на три секунды новее. Сверка " +
      `нашего пересчёта с опубликованным: ${diffLabel(check)}, ${accuracyLabel(check)}`,
  };
}

/**
 * Причина пустоты панели словами.
 *
 * «Нет данных» никогда не изображается нулём или пустым местом — сквозное
 * правило этого интерфейса: пустота на месте числа читается как ноль.
 */
export function emptyNote(v: Valuation | undefined): string {
  if (!v) return "загрузка…";
  if (v.seconds_left < 0) return "окно закончилось: сверять нечего";
  if (!v.check) return "сырой ряд не приехал: трейдер не подписан или поток молчит";
  return "";
}

/** Доллары для показа, два знака: панель сравнивает величины, а не цены ордера. */
function fmtUsd(micro: number): string {
  return (micro / SCALE).toFixed(2);
}

/**
 * Требуемый скачок словами: «$21» или «перевернуть нечем».
 *
 * Именно ДОЛЛАРЫ, а не сигмы: в сигмах это число выглядит недостижимым там, где
 * рынок его проходил, — на секундном горизонте хвосты у BTC толстые.
 */
export function requiredLabel(lock: WindowLock | undefined): string {
  if (!lock) return "—";
  // «Нечем» — по МОДАЛЬНОЙ задержке: окно итога закрылось, и величине двигать
  // уже некуда. Про запертость исхода это не говорит ничего — она наступает
  // секундой позже, и надпись замка в эту секунду называет обе.
  if (!lock.possible) return "перевернуть нечем";
  const required = parse(lock.required ?? "");
  if (required === null || required <= 0) return "—";
  return `${fmtUsd(required)} $`;
}

/**
 * Достижимое среднее рядом с требуемым скачком — второе число строки.
 *
 * # Зачем оно тут
 *
 * Это первое место, где оператор видит СООТНОШЕНИЕ, а не два несравнимых
 * числа. Требуемый скачок сам по себе не говорит ничего: $20 на спокойном
 * рынке недостижимы, а на рваном проходятся за десять секунд. Рядом с
 * достижимым средним за тот же горизонт он читается сразу.
 *
 * Именно СРЕДНЕЕ, а не пик: требование — к среднему по неизвестным секундам.
 * Пик остаётся в окне разбора как напоминание о толстых хвостах.
 *
 * Пусто, когда сравнивать не с чем: горизонта ещё не набралось.
 */
export function attainableLabel(lock: WindowLock | undefined): string {
  const v = parse(lock?.mean_move ?? "");
  if (v === null || v <= 0) return "";
  return `${fmtUsd(v)} $`;
}

/** Горизонт, за который взято достижимое среднее, — в подсказку. */
export function attainableTitle(lock: WindowLock | undefined): string {
  const v = parse(lock?.mean_move ?? "");
  if (v === null || v <= 0) return "";
  const h = lock?.mean_move_horizon_s;
  const over = h ? ` за ${h} с` : "";
  return (
    `достижимое СРЕДНЕЕ отклонение${over} — p90 наблюдённого. Сравнивать надо ` +
    "именно с ним: требуемый скачок — это требование к среднему за оставшиеся " +
    "секунды, а не к уровню. Ход вдвое больший, продержавшийся половину остатка, " +
    "даёт то же самое"
  );
}

/**
 * Вероятность переворота — процентом, и с пометкой «не сверено».
 *
 * # Почему пометка обязательна
 *
 * σ остатка оценивается по ряду СРЕДНИХ с лагом порядка ширины окна: секундные
 * приращения сглаженного ряда занижают её в восемь раз. Верна ли эта поправка,
 * проверяется только калибровкой задним числом — до первого сошедшегося отчёта
 * это модельное число, а не измеренное. Пометка снимается, когда наклон
 * наблюдённой частоты на предсказанную окажется в [0.7, 1.4].
 *
 * Значком, а не строкой, — тем же приёмом, что SOURCE_NOTE: место в липкой
 * полосе оплачено всей высотой экрана, а оговорку прочитывают один раз.
 *
 * Пусто, когда числа нет: разброс остатка не оценён.
 */
export const PROB_UNVERIFIED_NOTE =
  "вероятность ПЕРЕВОРОТА ведущей стороны, а не вероятность Up. Модельная и НЕ " +
  "СВЕРЕНА с фактической частотой: σ остатка оценена по ряду средних, и верность " +
  "поправки проверяется только отчётом задним числом (make forecast-report). " +
  "Решение по ней не принимается. Первое число — от сырого запаса, второе — от " +
  "запаса достроенного ряда, того же, что стоит слева";

export function probFlipLabel(lock: WindowLock | undefined): string {
  return percentOf(lock?.prob_flip);
}

/**
 * Та же вероятность по ДОСТРОЕННОМУ ряду — вторым числом через стрелку.
 *
 * # Почему без неё строка врала
 *
 * Вероятность считается от `|запас| / σ`, и запас у настоящего ряда и у
 * достроенного — разные числа: в быстром движении они расходятся до смены
 * знака. Пока поправки не было, панель показывала запас тени рядом с
 * вероятностью от СЫРОГО запаса, и два соседних числа говорили разное. Замер
 * 2026-08-25 21:58:50 на BTC: сырой запас +5.67 давал 35 % переворота, тень
 * −0.57 даёт 48 %, а стакан стоял на 0.50/0.49 — то есть рынку отвечала тень, а
 * показанное число расходилось с ним на пятнадцать пунктов.
 *
 * Пусто, когда сравнивать нечего: достройки нет, разброс остатка не оценён или
 * поправка округляется в тот же процент. Тем же правилом, что у остальных
 * поправок яруса: показывается то, что ОТЛИЧАЕТСЯ.
 */
export function aheadProbFlipLabel(lock: WindowLock | undefined): string {
  const p = lock?.ahead?.prob_flip;
  if (p === undefined || p <= 0) return "";
  const shown = percentOf(p);
  return shown === probFlipLabel(lock) ? "" : shown;
}

/** Доля в проценты: «0.023» на липкой полосе читается как цена. */
export function percentOf(p: number | undefined): string {
  if (p === undefined || p <= 0) return "";
  if (p < 0.01) return "<1 %";
  return `${Math.round(p * 100)} %`;
}

/**
 * Целевая цена площадки: куда должен уйти спот, чтобы исход перевернулся.
 *
 * В валюте САМОЙ площадки — потому и годится: требуемое движение это
 * приращение, а приращение от цены той же площадки не требует курса.
 *
 * Пусто, когда переводить не от чего: площадки нет либо поправка вырождается.
 */
export function venueTargetLabel(lock: WindowLock | undefined): string {
  const t = parse(lock?.venue_target ?? "");
  if (t === null || t <= 0) return "";
  const quote = lock?.venue_quote ? ` ${lock.venue_quote.toUpperCase()}` : "";
  return `${fmtUsd(t)}${quote}`;
}

/** Подсказка «на бирже уже поздно». Пусто, когда ещё не поздно. */
export function lateOnVenueNote(lock: WindowLock | undefined): string {
  if (!lock?.late_on_venue) return "";
  return "на бирже уже поздно: ход, начатый сейчас, в индекс не доедет";
}

/** Запас над страйком со знаком и стороной. */
export function cushionLabel(lock: WindowLock | undefined): string {
  const cushion = parse(lock?.cushion ?? "");
  if (cushion === null) return "—";
  if (cushion === 0) return "ровно на страйке";
  return `${cushion > 0 ? "выше" : "ниже"} на ${fmtUsd(Math.abs(cushion))} $`;
}

/**
 * Поправка замка по достроенному ряду — ВТОРЫМ числом, рядом с первым.
 *
 * # Почему рядом, а не вместо
 *
 * Требуемый скачок считается как `W·C/u`, и пока до конца окна больше `W + 2`
 * секунд, всё окно усреднения ещё впереди: прогноз итога вырождается в текущий
 * уровень индекса, а требуемый скачок — в `|индекс − страйк|`. То есть почти всё
 * окно точность решающего числа равна точности знания УРОВНЯ прямо сейчас, а её
 * достройка режет вдвое (RMSE $4.30 против $2.15, замер 2026-08-14).
 *
 * Но сегодняшнее число обязано остаться на экране: иначе поправка перестанет
 * читаться как поправка, и оператор не увидит, НАСКОЛЬКО провод отстал.
 *
 * Пусто — когда сравнивать нечего: достройки нет, она себя выключила, или
 * поправка округляется в то же число.
 */
export function aheadRequiredLabel(lock: WindowLock | undefined): string {
  const a = lock?.ahead;
  if (!a || sameCents(lock?.required, a.required)) return "";
  if (!a.possible) return "перевернуть нечем";
  const required = parse(a.required ?? "");
  if (required === null || required <= 0) return "";
  return `${fmtUsd(required)} $`;
}

/** То же для запаса над страйком. */
export function aheadCushionLabel(lock: WindowLock | undefined): string {
  const a = lock?.ahead;
  if (!a || sameCents(lock?.cushion, a.cushion)) return "";
  const cushion = parse(a.cushion ?? "");
  if (cushion === null) return "";
  if (cushion === 0) return "ровно на страйке";
  return `${cushion > 0 ? "выше" : "ниже"} на ${fmtUsd(Math.abs(cushion))} $`;
}

/** Совпадают ли два числа С ТОЧНОСТЬЮ ДО ЦЕНТА, то есть до того, что видно. */
function sameCents(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return true;
  const x = parse(a);
  const y = parse(b);
  if (x === null || y === null) return true;
  return fmtUsd(x) === fmtUsd(y);
}

/**
 * Фора словами: на сколько раньше провода мы знаем это число.
 *
 * Число приходит с сервера уже по СТРОГОМУ краю интервала лага — на секунду
 * меньше замеренного. Так и надо: `ℓ` внутри интервала [0 с, 1 с] неразличим, а
 * каждая его секунда — это ровно секунда форы, и обещать оператору запас,
 * которого может не быть, нельзя.
 *
 * Пусто, когда форы нет: подсистема греется, разошлась или спот молчит.
 */
export function forecastGainLabel(check: TwapCheck | undefined): string {
  const f = check?.forecast;
  if (!f || f.state !== "живой" || !f.gain_ms) return "";
  return `фора ${(f.gain_ms / 1000).toFixed(1)} с`;
}

/**
 * Почему поправки нет — СЛОВАМИ.
 *
 * Пустое место на месте числа читается как «поправки не требуется», а это
 * другое утверждение: её просто не посчитали. Пусто здесь означает «всё в
 * порядке, показывать нечего сверх самой поправки».
 */
export function forecastNote(check: TwapCheck | undefined): string {
  const f = check?.forecast;
  if (!f) return "";
  switch (f.state) {
    case "живой":
      return "";
    case "грею":
      return "достройка греется";
    case "молчит":
      return "спот молчит: достраивать нечем";
    case "диверг": {
      const err = parse(f.err_median ?? "");
      const by = err === null ? "" : ` на ${fmtUsd(err)} $`;
      return `достройка отключена: расходится с проводом${by}`;
    }
    default:
      return "";
  }
}

/** Тон поправки: выключившаяся достройка красится тревожно, греющаяся — нет. */
export function forecastTone(check: TwapCheck | undefined): "flat" | "warn" {
  return check?.forecast?.state === "диверг" ? "warn" : "flat";
}

/**
 * Жива ли достройка — то есть вправе ли карточка ДЕРЖАТЬ ПОД НЕЁ МЕСТО.
 *
 * Вопрос отдельный от «есть ли что показать» (forecastValue), и разница
 * посекундная: совпав с проводом до цента, поправка не рисуется — законно и то
 * и дело. Слот при этом обязан остаться, иначе половина строки ездит туда-сюда
 * каждую секунду, а по числам в ней принимают решение.
 *
 * Выключенная же подсистема — это надолго, и место под неё держать нельзя:
 * дыра в четверть строки, ждущая числа все пять минут, читается как поломка.
 *
 * Этим же признаком держатся слоты ЯРУСА ЗАМКА, хотя тень туда приходит
 * отдельным полем (`lock.ahead`). Наличие поля в слоты не годится: сервер
 * кладёт его только когда достройке досталась хоть одна спот-секунда, а при
 * форе около секунды две ленты по 1 Гц расходятся фазой и то и дело не дают ни
 * одной. Состояние подсистемы через такую секунду не мигает, поле — мигает.
 */
export function forecastLive(check: TwapCheck | undefined): boolean {
  return check?.forecast?.state === "живой";
}

/**
 * Достроенный отчёт для показа: то же число, что «предикшен», но по ряду,
 * продолженному спотом. Пусто, когда публиковать его сейчас не вправе.
 */
export function forecastValue(check: TwapCheck | undefined): string | undefined {
  const f = check?.forecast;
  if (!f || f.state !== "живой" || !f.price) return undefined;
  // Совпало до цента — показывать нечего: две одинаковые цифры через стрелку
  // читаются как дефект показа, а не как «провод и оценка сошлись». Так бывает
  // законно: если достраивать было нечем, оценка вырождается в сегодняшнее
  // число по построению.
  if (sameCents(f.price, check?.ahead)) return undefined;
  return f.price;
}

/**
 * Куда метрика придёт, если цена замрёт, — ПРЕДЕЛ и путь к нему.
 *
 * # Зачем это читать
 *
 * Разница между опубликованным числом и сырым — это снос, УЖЕ ЗАЛОЖЕННЫЙ в
 * метрику: он случится, даже если рынок больше не шевельнётся, просто потому что
 * старые секунды покидают окно усреднения. Понимать его размер надо до того, как
 * принимать решение по «запасу»: половина движения на экране может оказаться
 * уже свершившейся.
 *
 * Предел показывается отдельно от пути, и это не украшение: предел тривиален
 * (среднее постоянного ряда равно этой постоянной, то есть сырой цене), а путь
 * идёт С ПЕРЕЛЁТОМ — форма зависит от того, какие секунды выпадают из окна, и
 * линейной интерполяцией не восстанавливается.
 */
export function frozenLimit(check: TwapCheck | undefined): string | undefined {
  const hs = frozenHorizons(check);
  return hs.length === 0 ? undefined : check!.frozen![String(hs[hs.length - 1])];
}

/** Путь к пределу: ближние горизонты через запятую. Пусто — пути нет. */
export function frozenPath(check: TwapCheck | undefined): string {
  const hs = frozenHorizons(check);
  if (hs.length < 2) return "";
  return hs
    .slice(0, -1)
    .map((h) => `+${h} с ${fmtUsd(parse(check!.frozen![String(h)]) ?? 0)}`)
    .join(", ");
}

/** Через сколько секунд метрика придёт к пределу. Пусто, когда предела нет. */
export function frozenLimitNote(check: TwapCheck | undefined): string {
  const hs = frozenHorizons(check);
  if (hs.length === 0) return "";
  return `через ${hs[hs.length - 1]} с сверх того, что уже знаем`;
}

/** Горизонты по возрастанию. Ключи приходят строками — JSON чисел не хранит. */
function frozenHorizons(check: TwapCheck | undefined): number[] {
  const frozen = check?.frozen;
  if (!frozen) return [];
  return Object.keys(frozen)
    .map(Number)
    .filter((h) => Number.isFinite(h) && h > 0)
    .sort((a, b) => a - b);
}

/** Валюта и возраст опорного ряда — для окна разбора. */
export function forecastSpotNote(f: IndexForecast): string {
  const parts: string[] = [];
  if (f.spot_quote) parts.push(f.spot_quote.toUpperCase());
  if (f.estimated) parts.push(`достроено ${f.estimated} с`);
  return parts.join(", ");
}

/** Медиана расхождения самосверки числом. */
export function forecastErrValue(f: IndexForecast): string {
  const med = parse(f.err_median ?? "");
  if (med === null || !f.err_checks) return "—";
  return `${fmtUsd(med)} $`;
}

/**
 * Подпись под самосверкой: p90 и сколько сверок за ней стоит.
 *
 * Число сверок обязательно: медиана по трём сверкам и по шестидесяти — разные
 * утверждения, а на экране они выглядят одинаково.
 */
export function forecastErrNote(f: IndexForecast): string {
  if (!f.err_checks) return "сверок ещё не было";
  const p90 = parse(f.err_p90 ?? "");
  const tail = p90 === null ? "" : `p90 ${fmtUsd(p90)} $, `;
  const rel = forecastErrRel(f);
  const head = rel === null ? "" : `×${rel.toFixed(2)} от «ничего не делать», `;
  return `${head}${tail}${f.err_checks} сверок`;
}

/**
 * Самосверка в долях от «ничего не делать»: null означает «планки нет».
 *
 * Доллары на экране остаются, но читать по ним, уехала ли модель, нельзя: они
 * движутся и от цены актива, и от волатильности часа. Замер по двум записям
 * провода — в долларах ошибка выросла в 2.6 раза, а в долях от планки
 * УМЕНЬШИЛАСЬ (0.62 → 0.48 у BTC, 0.63 → 0.53 у ETH). Единица означает
 * «достройка не лучше, чем ничего не делать», и другой планки этому числу не
 * нужно.
 *
 * Нулевая планка означает, что рынок стоял: делить нечего, и «бесконечно хуже»
 * было бы утверждением про рынок, выданным за утверждение про модель.
 */
export function forecastErrRel(f: IndexForecast): number | null {
  const med = parse(f.err_median ?? "");
  const base = parse(f.err_base ?? "");
  if (med === null || base === null || base <= 0) return null;
  return med / base;
}

/**
 * Разложение самосверки по плечу оценки одной строкой.
 *
 * Пустая строка означает «разложения нет». Плечо в секундах: миллисекунды тут
 * были бы точностью, которой у провода нет, — он прорежен до 1 Гц.
 */
export function forecastLeverNote(f: IndexForecast): string {
  const by = f.err_by_lever ?? [];
  if (by.length === 0) return "";
  return by
    .map((l) => {
      const med = parse(l.median ?? "");
      const value = med === null ? "—" : `${fmtUsd(med)} $`;
      return `${Math.round(l.lever_ms / 1000)} с: ${value} ×${l.checks}`;
    })
    .join(" · ");
}

/**
 * Возраст сырого значения считается от ЕГО метки, а не от метки метрики.
 *
 * Ряды разные и приезжают порознь: сырой может молчать при живой метрике и
 * наоборот. NaN означает «метки нет» — ровно как indexAge в lib/valuation.ts.
 */
export function rawAge(check: TwapCheck | undefined, nowMs: number): number {
  const at = check?.raw_at;
  if (!at) return Number.NaN;
  const ms = Date.parse(at);
  return Number.isNaN(ms) ? Number.NaN : nowMs - ms;
}

/**
 * Докуда у числа дотягиваются НАСТОЯЩИЕ данные, в секундах назад от его метки.
 *
 * # Зачем подписывать этим, а не «предикшен» и «достройка»
 *
 * Оба числа — один и тот же отчёт, посчитанный по-разному, и различает их ровно
 * одно: свежесть последней настоящей секунды в окне усреднения.
 *
 *   у отчёта по проводу окно кончается на `t−3` (задержка публикации), и все
 *   его входы настоящие;
 *
 *   у достроенного окно кончается там же, но метка `t` сдвинута вперёд на число
 *   дописанных секунд — значит последняя НАСТОЯЩАЯ секунда отстоит от неё на
 *   `3 + оценок`.
 *
 * То есть достройка меняет свежесть на дальность, и подпись обязана это
 * называть: без неё второе число выглядит просто «лучше», а оно другое.
 */
export const REAL_DATA_LAG_S = 3;

/** У отчёта по проводу все входы настоящие, и свежесть у него постоянная. */
export function realDataLagLabel(): string {
  return `t−${REAL_DATA_LAG_S}`;
}

export function forecastLagLabel(check: TwapCheck | undefined): string {
  const est = check?.forecast?.estimated ?? 0;
  if (est <= 0) return "";
  return `t−${REAL_DATA_LAG_S + est}`;
}

/**
 * Отклонение числа от ОПУБЛИКОВАННОГО — со знаком, для показа в скобках.
 *
 * Опубликованное и есть «текущее»: это то, что видит биржа и по чему она
 * резолвит. Разница с ним отвечает на вопрос «на сколько метрика вот-вот
 * сдвинется», а он и есть причина смотреть на оба числа.
 */
export function vsCurrentLabel(
  value: string | null | undefined,
  current: string | null | undefined,
): string {
  const d = diffMicro(value, current);
  if (d === null) return "";
  const rounded = fmtUsd(Math.abs(d));
  if (rounded === "0.00") return "0.00";
  return `${d > 0 ? "+" : "−"}${rounded}`;
}

/** Плюс — зелёное, минус — красное, ноль и «нет данных» — приглушённое. */
export function vsCurrentTone(
  value: string | null | undefined,
  current: string | null | undefined,
): "up" | "down" | "flat" {
  const d = diffMicro(value, current);
  if (d === null) return "flat";
  if (fmtUsd(Math.abs(d)) === "0.00") return "flat";
  return d > 0 ? "up" : "down";
}

function diffMicro(
  value: string | null | undefined,
  current: string | null | undefined,
): number | null {
  const a = parse(value);
  const b = parse(current);
  if (a === null || b === null) return null;
  return a - b;
}

/**
 * Куда должен пойти индекс, чтобы исход ПЕРЕВЕРНУЛСЯ, — и каким это красить.
 *
 * Запас положителен, когда прогноз итога выше страйка (исход Up): перевернуть
 * его можно только ходом ВНИЗ. Цвет по направлению хода, а не по величине:
 * «расти» на терминале красное, «падать» зелёное, и здесь эта привычка ровно к
 * месту — она отвечает на вопрос «куда должна пойти цена», а не «хорошо ли
 * это».
 */
export function flipTone(cushion: string | null | undefined): "up" | "down" | "flat" {
  const c = parse(cushion);
  if (c === null || c === 0) return "flat";
  // Запас выше нуля — переворот требует падения; ниже — роста.
  return c > 0 ? "down" : "up";
}

/** Направление переворота словами — в подсказку. */
export function flipTitle(cushion: string | null | undefined): string {
  const c = parse(cushion);
  if (c === null) return "";
  if (c === 0) return "исход ровно на грани";
  return c > 0
    ? "чтобы перевернуть исход, индекс должен УПАСТЬ на эту величину и удержаться"
    : "чтобы перевернуть исход, индекс должен ВЫРАСТИ на эту величину и удержаться";
}

/**
 * Какой будет исход, если цена замрёт. Пусто, когда считать не из чего.
 *
 * Считается из знака запаса, а не из `outcome`: тот заполняется только когда
 * исход уже ЗАПЕРТ, а вопрос «куда идёт дело прямо сейчас» задают всё окно.
 */
export function frozenOutcome(lock: WindowLock | undefined): string {
  const c = parse(lock?.cushion ?? "");
  if (c === null || c === 0) return "";
  return c > 0 ? "Up" : "Down";
}

/**
 * Запас над страйком — СТРЕЛКОЙ и величиной, без слов.
 *
 * Стрелка та же и значит то же, что в строке индекса выше: вверх зелёное, вниз
 * красное. «Выше на»/«ниже на» стоили одиннадцать знаков липкой строки и
 * сообщали ровно то, что стрелка говорит значком, — а знак читается раньше слов.
 */
export function cushionArrow(cushion: string | null | undefined): string {
  const c = parse(cushion);
  if (c === null) return "";
  if (c === 0) return "=";
  return c > 0 ? "▲" : "▼";
}

/** Тон запаса: выше страйка — зелёное, ниже — красное. */
export function cushionTone(cushion: string | null | undefined): "up" | "down" | "flat" {
  const c = parse(cushion);
  if (c === null || c === 0) return "flat";
  return c > 0 ? "up" : "down";
}

/** Величина запаса без знака: направление уже сказала стрелка. */
export function cushionAmount(cushion: string | null | undefined): string {
  const c = parse(cushion);
  if (c === null) return "—";
  return `${fmtUsd(Math.abs(c))} $`;
}

/** Тот же запас по достроенному ряду. Пусто, когда совпал до цента. */
export function aheadCushionAmount(lock: WindowLock | undefined): string {
  const a = lock?.ahead;
  if (!a || sameCents(lock?.cushion, a.cushion)) return "";
  return `${cushionArrow(a.cushion)} ${cushionAmount(a.cushion)}`;
}

/** Запас словами — в подсказку, вместе с поправкой достройки. */
export function cushionTitle(lock: WindowLock | undefined): string {
  const base =
    `прогноз итога против страйка, если цена замрёт: ${cushionLabel(lock)}` +
    ". Стрелка вверх — итог ВЫШЕ страйка";
  const fwd = aheadCushionLabel(lock);
  return fwd === "" ? base : `${base}. По достроенному ряду: ${fwd}`;
}

/**
 * Ожидаемое ЗНАЧЕНИЕ метрики на конец окна, если цена замрёт.
 *
 * Показывается вместо исхода «Up/Down», и это не косметика. Исход — производная
 * от сравнения со страйком, а страйк стоит рядом; само же число отвечает на
 * вопрос, которого сравнение не закрывает: НАСКОЛЬКО итог разойдётся со страйком
 * и сколько до него осталось. Сторона при этом не теряется — её говорит стрелка
 * у запаса, а словами она стоит в подсказке.
 *
 * Метрика та, по которой резолвится ЭТОТ рынок: TWAP-30 или TWAP-60 — окно
 * берётся из справочника, а не из длительности рынка.
 */
export function projectedValue(lock: WindowLock | undefined): string | undefined {
  return lock?.projected || undefined;
}

/** Тот же итог по достроенному ряду. Пусто, когда совпал до цента. */
export function aheadProjectedValue(lock: WindowLock | undefined): string | undefined {
  const a = lock?.ahead;
  if (!a?.projected || sameCents(lock?.projected, a.projected)) return undefined;
  return a.projected;
}

// ---------------------------------------------------------------------------
// Итог ЗАКОНЧИВШЕГОСЯ окна
// ---------------------------------------------------------------------------

/**
 * Снос итога от страйка со знаком: «+1.10 $» или «−0.77 $».
 *
 * Знак здесь ОБЯЗАТЕЛЕН и он же назначает сторону: окно резолвится сравнением
 * итога метрики со страйком, и это единственная величина, из которой исход
 * следует. Пустая строка — сноса нет: страйк не записан либо итог не приехал.
 *
 * Ровно на страйке — «ровно на страйке», а не «+0.00»: рынок в этот момент
 * резолвится в Up, и написать нулевой снос знаком «+» значило бы изобразить
 * перевес там, где его нет.
 */
export function finalDriftLabel(final: WindowFinal | undefined): string {
  const d = parse(final?.drift ?? "");
  if (d === null) return "";
  if (d === 0) return "ровно на страйке";
  return `${d > 0 ? "+" : "−"}${fmtUsd(Math.abs(d))} $`;
}

/**
 * Расчётная сторона окна по знаку сноса: «Up» либо «Down».
 *
 * Это НЕ исход биржи, а то, что следует из чисел, и различать их обязательно:
 * подтверждение из Gamma доезжает до двух минут, и всё это время на экране может
 * стоять только расчётная сторона. Имена исходов проект берёт из справочника
 * рынка, но здесь их всего два и они заданы самой серией up-or-down.
 */
export function finalSideLabel(final: WindowFinal | undefined): string {
  const d = parse(final?.drift ?? "");
  if (d === null) return "";
  // Ровно на страйке рынок резолвится в Up: сравнение нестрогое.
  return d >= 0 ? "Up" : "Down";
}

/**
 * Исход окна словами: «исход Up» либо «исход биржей не подтверждён».
 *
 * Пустой winner при resolved означает «подтверждения не дождались за сутки» —
 * окно снято, отменено либо исчезло из справочника. Это тоже не «проиграл».
 */
export function finalOutcomeLabel(final: WindowFinal | undefined): string {
  if (!final) return "";
  if (!final.resolved) return "исход биржей не подтверждён";
  if (!final.winner) return "исход подтверждён, но сторону биржа не назвала";
  return `исход ${final.winner}`;
}

/**
 * Сошлись ли расчётная сторона и исход биржи.
 *
 * `null` — сравнивать нечем (исхода нет либо сноса нет). Расхождение означает не
 * «модель плоха», а что мы читаем не тот отчёт: сторона выводится из тех же двух
 * чисел, по которым рынок и резолвится, — и увидеть такое надо на экране, а не
 * в логах.
 */
export function finalAgrees(final: WindowFinal | undefined): boolean | null {
  if (!final?.resolved || !final.winner) return null;
  const side = finalSideLabel(final);
  if (!side) return null;
  return side.toLowerCase() === final.winner.trim().toLowerCase();
}

/**
 * Тон сноса: та же система знаков, что у запаса и стрелки отклонения.
 *
 * Живёт здесь, а не в компоненте, по правилу модуля: компонентных тестов в
 * проекте нет, и всё, о чём можно ошибиться, обязано быть в .ts.
 */
export function finalDriftTone(final: WindowFinal | undefined): "up" | "down" | "flat" {
  const d = parse(final?.drift ?? "");
  if (d === null || d === 0) return "flat";
  return d > 0 ? "up" : "down";
}

/** Тон стороны-победителя по её имени. Пусто — «flat». */
export function finalWinnerTone(final: WindowFinal | undefined): "up" | "down" | "flat" {
  const w = final?.winner?.trim().toLowerCase();
  if (!w) return "flat";
  if (w === "down") return "down";
  if (w === "up") return "up";
  return "flat";
}

/** Подсказка к строке итога: чем измерено, чем проверено и почему прогноза нет. */
export function finalTitle(v: Valuation | undefined): string {
  const f = v?.final;
  if (!f) return "";
  const parts = [
    "значение метрики с меткой КОНЦА окна — то самое число, по которому рынок и " +
      "резолвится. Оно не меняется: прогноз, фора и сверка отвечали на вопросы " +
      "идущего окна и считались от текущей секунды",
  ];
  if (f.replay) {
    parts.push(
      `пол пересчёта ${f.replay} — то же число по полному ряду, дочитанному до ` +
        "конца. Расхождение с итогом означает дыры в проводе, а не ошибку модели",
    );
  }
  if (f.settled_at) parts.push(`исход сведён ${f.settled_at}`);
  const agrees = finalAgrees(f);
  if (agrees === false) {
    parts.push(
      "РАСХОЖДЕНИЕ: сторона по сносу не совпала с исходом биржи. Это значит, что " +
        "мы читаем не тот отчёт, а не что модель ошиблась",
    );
  }
  return parts.join(". ");
}
